import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridFormPopupSizeKey
} from '../opsDataGrid';

export interface ClientMentionContactRow {
  id: number;
  name: string;
  email: string;
  mobile?: string | null;
  description?: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface ClientMentionContactUpsertBody {
  name: string;
  email: string;
  mobile?: string | null;
  description?: string | null;
  isActive: boolean;
}

export interface ClientMentionContactsAdminApi {
  list: () => Promise<ClientMentionContactRow[]>;
  create: (body: ClientMentionContactUpsertBody) => Promise<ClientMentionContactRow>;
  update: (id: number, body: ClientMentionContactUpsertBody) => Promise<ClientMentionContactRow>;
  delete: (id: number) => Promise<void>;
}

export interface ClientMentionContactsAdminGridProps {
  api: ClientMentionContactsAdminApi;
  stateStorageKey: string;
}

const CLIENT_MENTION_CONTACTS_GRID_SCHEMA_VERSION = 1;
const CLIENT_MENTION_CONTACTS_GRID_COLUMNS = [
  'name',
  'email',
  'mobile',
  'description',
  'isActive',
  'modifiedAt',
  'modifiedBy'
] as const;

const emptyForm = (): ClientMentionContactUpsertBody => ({
  name: '',
  email: '',
  mobile: '',
  description: '',
  isActive: true
});

const fieldLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--ops-color-text, #111827)'
};

export function ClientMentionContactsAdminGrid({ api, stateStorageKey }: ClientMentionContactsAdminGridProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [rows, setRows] = useState<ClientMentionContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ClientMentionContactUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list());
    } catch (e) {
      setRows([]);
      setFormError(e instanceof Error ? e.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (row: ClientMentionContactRow) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      email: row.email,
      mobile: row.mobile ?? '',
      description: row.description ?? '',
      isActive: row.isActive
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: ClientMentionContactUpsertBody = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile?.trim() || null,
        description: form.description?.trim() || null,
        isActive: form.isActive
      };
      if (editingId == null) {
        await api.create(body);
      } else {
        await api.update(editingId, body);
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (editingId == null) return;
    if (!window.confirm('Delete this mention contact?')) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.delete(editingId);
      setEditorOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && rows.length === 0 ? (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
          <LoadIndicator visible height={20} width={20} />
          <span>Loading…</span>
        </div>
      ) : (
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          noDataText="No mention contacts — click Add contact to create one."
          onRowDblClick={(e) => onGridRowDblClick(e, openEdit)}
          onKeyDown={(e) => onGridFocusedRowEnter(e, openEdit)}
        >
          <OpsGridStateStoring
            storageKey={stateStorageKey}
            schemaVersion={CLIENT_MENTION_CONTACTS_GRID_SCHEMA_VERSION}
            columns={CLIENT_MENTION_CONTACTS_GRID_COLUMNS}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh contacts"
              newItem={{ hint: 'Add contact', onClick: openCreate }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={240} placeholder="Search contacts…" />
          <HeaderFilter visible />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Paging defaultPageSize={25} />
          <Column dataField="name" caption="Name" minWidth={140} />
          <Column dataField="email" caption="Email" minWidth={180} />
          <Column dataField="mobile" caption="Mobile" minWidth={120} />
          <Column dataField="description" caption="Description" minWidth={160} />
          <Column dataField="isActive" caption="Active" dataType="boolean" width={90} />
          <Column dataField="modifiedAt" caption="Modified" dataType="datetime" width={170} />
          <Column dataField="modifiedBy" caption="Modified by" minWidth={120} visible={false} />
        </DataGrid>
      )}

      <GridFormPopup
        visible={editorOpen}
        title={editingId == null ? 'Add mention contact' : 'Edit mention contact'}
        onClose={() => setEditorOpen(false)}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={formError}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div>
              {editingId != null ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button text="Cancel" disabled={saving} onClick={() => setEditorOpen(false)} />
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving}
                onClick={() => void save()}
              />
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label>
            <span style={fieldLabel}>Name</span>
            <TextBox value={form.name} onValueChanged={(e) => setForm((f) => ({ ...f, name: e.value ?? '' }))} />
          </label>
          <label>
            <span style={fieldLabel}>Email</span>
            <TextBox value={form.email} onValueChanged={(e) => setForm((f) => ({ ...f, email: e.value ?? '' }))} />
          </label>
          <label>
            <span style={fieldLabel}>Mobile</span>
            <TextBox
              value={form.mobile ?? ''}
              onValueChanged={(e) => setForm((f) => ({ ...f, mobile: e.value ?? '' }))}
            />
          </label>
          <label>
            <span style={fieldLabel}>Description</span>
            <TextBox
              value={form.description ?? ''}
              onValueChanged={(e) => setForm((f) => ({ ...f, description: e.value ?? '' }))}
            />
          </label>
          <CheckBox
            text="Active"
            value={form.isActive}
            onValueChanged={(e) => setForm((f) => ({ ...f, isActive: !!e.value }))}
          />
        </div>
      </GridFormPopup>
    </>
  );
}
